/**
 * Copyright (c) 2025 Celestis AI
 *
 * WARNING: Unauthorized copying, modification, distribution, or use of this
 * software, via any medium, is strictly prohibited without explicit written
 * permission from Celestis AI.
 *
 * All rights reserved.
 */

import { getRequiredEnv, getOptionalIntEnv, getOptionalBoolEnv } from '@/utils/env';

// API URL - must be set via environment variable
export const API_URL = getRequiredEnv('VITE_API_BASE_URL', 'Backend API server URL');

// UI URL - must be set via environment variable
export const UI_URL = getRequiredEnv('VITE_UI_BASE_URL', 'Frontend UI base URL');

// Helper function: Check if running in production
export const isProduction = (): boolean => {
  const uiBaseUrl = UI_URL;
  const isProductionInEnv = uiBaseUrl?.includes('console.celestis.ai');
  
  // In browser, also check window.location for consistency
  if (typeof window !== 'undefined') {
    const isProductionInHostname = window.location.hostname.includes('console.celestis.ai');
    
    // If they don't match, something is wrong
    if (isProductionInEnv !== isProductionInHostname) {
      console.error(
        '[ENVIRONMENT MISMATCH] VITE_UI_BASE_URL and window.location.hostname do not match:',
        {
          uiBaseUrl,
          hostname: window.location.hostname,
          envSaysProduction: isProductionInEnv,
          hostnameSaysProduction: isProductionInHostname
        }
      );
    }
    
    // Use hostname as source of truth in browser
    return isProductionInHostname;
  }
  
  // Fallback to env var if not in browser
  return isProductionInEnv || false;
};

export const POLLING_CONFIG = {
  /**
   * Per-user credits SSE (`GET /users/{id}/credits/stream`). Defaults **on** in dev and production.
   * Set `VITE_USER_CREDITS_SSE=false` (or any value other than true/1/yes) to use polling instead.
   */
  userCreditsSseEnabled: getOptionalBoolEnv('VITE_USER_CREDITS_SSE', true),

  // Credit balance refresh interval (paused when tab is hidden)
  creditRefreshInterval: getOptionalIntEnv('VITE_CREDIT_REFRESH_INTERVAL', 120000),
  
  // Ingest status polling during active ingestion (per-ProjectCard)
  ingestActiveInterval: getOptionalIntEnv('VITE_INGEST_ACTIVE_POLL_INTERVAL', 30000),
  
  // Initial ingest polling delay
  ingestInitialDelay: getOptionalIntEnv('VITE_INGEST_INITIAL_POLL_DELAY', 5000),
  
  // Ingest logs polling interval (for progress bar during active ingestion)
  ingestLogsInterval: getOptionalIntEnv('VITE_INGEST_LOGS_POLL_INTERVAL', 30000),
  
  // Analytics logs auto-refresh interval
  analyticsLogsInterval: getOptionalIntEnv('VITE_ANALYTICS_LOGS_POLL_INTERVAL', 60000),
  
  // Capsule logs polling interval (stale time for react-query)
  capsuleLogsStaleTime: getOptionalIntEnv('VITE_CAPSULE_LOGS_STALE_TIME', 30000),
} as const;

// Stripe configuration
